import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { FileText, Clock, AlertCircle, ArrowRight, Filter, CheckCircle, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StudentOverview {
  sophomorixUnid: string;
  student_name: string;
  student_first_name: string;
  student_class: string;
  pending_count: number;
  oldest_submission: string;
  newest_submission: string;
  categories: string | null;
}

export default function StudentFilesReviewDashboard() {
  const navigate = useNavigate();
  const [filterClass, setFilterClass] = useState<string>('');
  const [sortBy, setSortBy] = useState<'count' | 'oldest' | 'name'>('count');

  const { data, isLoading, error } = useQuery(
    ['student-files-review-dashboard'],
    async () => {
      const response = await api.get('/student-files/queue/students-overview');
      return response.data;
    },
    {
      retry: 3,
      refetchInterval: 30000,
    }
  );

  const students: StudentOverview[] = data?.data || [];
  const totalPending = data?.total_pending || 0;

  // Filter and sort
  const filteredStudents = students
    .filter(s => !filterClass || s.student_class === filterClass)
    .sort((a, b) => {
      switch (sortBy) {
        case 'count':
          return b.pending_count - a.pending_count;
        case 'oldest':
          return new Date(a.oldest_submission).getTime() - new Date(b.oldest_submission).getTime();
        case 'name':
          return `${a.student_first_name} ${a.student_name}`.localeCompare(`${b.student_first_name} ${b.student_name}`);
        default:
          return 0;
      }
    });

  const uniqueClasses = Array.from(new Set(students.map(s => s.student_class))).sort();

  const getWaitingTime = (dateString: string) => {
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Heute';
    if (days === 1) return 'Gestern';
    return `vor ${days} Tagen`;
  };

  const getUrgencyColor = (days: number) => {
    if (days >= 7) return 'text-red-600 bg-red-50 border-red-200';
    if (days >= 3) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Fehler beim Laden der Übersicht</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6">
        <h1 className="text-3xl font-bold flex items-center mb-2">
          <FileText className="h-8 w-8 mr-3" />
          Aktenprüfung - Übersicht
        </h1>
        <p className="text-blue-100">
          {totalPending} ausstehende Datei{totalPending !== 1 ? 'en' : ''} bei {students.length} Schüler{students.length !== 1 ? 'n' : ''}
        </p>
      </div>

      {/* Filters */}
      {students.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">Klasse:</label>
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="input input-sm border-gray-300"
              >
                <option value="">Alle Klassen</option>
                {uniqueClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Sortieren nach:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="input input-sm border-gray-300"
              >
                <option value="count">Anzahl (höchste zuerst)</option>
                <option value="oldest">Älteste zuerst</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Students List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStudents.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg border border-gray-200 p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium">Keine ausstehenden Prüfungen</p>
            <p className="text-sm text-gray-500 mt-2">
              Alle eingereichten Dateien wurden bearbeitet.
            </p>
          </div>
        ) : (
          filteredStudents.map((student) => {
            const daysWaiting = Math.floor(
              (Date.now() - new Date(student.oldest_submission).getTime()) / (1000 * 60 * 60 * 24)
            );
            const urgencyClass = getUrgencyColor(daysWaiting);

            return (
              <div
                key={student.sophomorixUnid}
                className={`bg-white rounded-lg border-2 ${urgencyClass} p-4 hover:shadow-lg transition-shadow cursor-pointer`}
                onClick={() => navigate(`/Akten?student=${student.sophomorixUnid}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">
                      {student.student_first_name} {student.student_name}
                    </h3>
                    <p className="text-sm opacity-75">Klasse: {student.student_class}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{student.pending_count}</div>
                    <div className="text-xs opacity-75">Datei{student.pending_count !== 1 ? 'en' : ''}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>Älteste: {getWaitingTime(student.oldest_submission)}</span>
                  </div>
                  {student.categories && (
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span className="truncate">{student.categories}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-current border-opacity-20">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/Akten?student=${student.sophomorixUnid}`);
                    }}
                    className="w-full btn btn-sm flex items-center justify-center"
                  >
                    Zur Akte
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
